/**
 * useEventHierarchy — what the Event Detail page shows of an event's place in the campaign hierarchy,
 * loaded once per event and shared by the Event Hierarchy field, the Parent Event field and the
 * page's event navigator.
 *
 * The event's node is looked up once (ListCampaignRoots?eventId=) and serves both the tree and the
 * parent — it carries `parentNodeId` — so the page makes that call once, not once per consumer.
 * After it, three independent reads, so a failure costs one thing rather than all of them:
 *
 *   nodes        the tree beneath the event (hierarchySync `loadHierarchy`).
 *   subEvents    ListSubEvents — every event beneath it the caller may see, with names. The tree has
 *                no names (a node carries only an event id), so this is where the tree's event rows
 *                get theirs. Failure leaves the rows unnamed and no navigator; the tree still shows.
 *   parentEvent  the event directly above (hierarchySync `parentEventIdOf`), then its GetEvent for
 *                the name. Skipped when the caller already knows it (`knownParent`): a child's tab
 *                on its parent's page has the parent on screen already.
 *
 * `eventTree` — the navigator — is every event beneath this one, at any depth, as a tree: `nodes` cut
 * to the events ListSubEvents returned, with external campaigns kept as grouping rows (utils/eventTree
 * says exactly how). Only those ListSubEvents returned, so a BD gets no row for an event the service
 * hides from them (a DRAFT, or another BU's), nor for anything beneath one.
 */

import { useEffect, useMemo, useState } from 'react';
import { eventHubCalendarClient, type SubEvent } from '../client/EventCalendarClient';
import { hierarchyUnder, parentEventIdOf } from '../components/CampaignHierarchy/hierarchySync';
import type { HierarchyNode } from '../components/CampaignHierarchy/campaignHierarchy';
import { visibleEventTree, type EventTreeRow } from '../utils/eventTree';

/** undefined while loading, null when there is none, 'error' when it could not be read. */
export type ParentEventState = { eventId: string; eventName?: string } | null | 'error' | undefined;

export interface EventHierarchy {
  /** The tree beneath the event: undefined while loading, null if it could not be read. */
  nodes: HierarchyNode[] | null | undefined;
  /**
   * The event's own node id: undefined while loading or if it could not be read, null when the event
   * has no node. Lets Create Child Event place the child without looking the node up again.
   */
  anchorNodeId: string | null | undefined;
  /** Names of the events beneath, by id — for the tree's event rows. */
  eventNames: ReadonlyMap<string, string>;
  /** Every event beneath, as a tree, for the navigator (see the header). Empty until both reads are in. */
  eventTree: EventTreeRow[];
  parentEvent: ParentEventState;
}

/** Event id → name, for every sub-event that has both. */
export function subEventNames(subEvents: readonly SubEvent[] | undefined): Map<string, string> {
  const names = new Map<string, string>();
  for (const s of subEvents ?? []) {
    if (s.event?.eventId && s.event.eventName) names.set(s.event.eventId, s.event.eventName);
  }
  return names;
}

export interface UseEventHierarchyOptions {
  /** The parent event, when the caller already has it; the parent reads are then skipped. */
  knownParent?: ParentEventState;
}

export function useEventHierarchy(
  eventId: string | undefined,
  { knownParent }: UseEventHierarchyOptions = {},
): EventHierarchy {
  const [nodes, setNodes] = useState<HierarchyNode[] | null | undefined>(undefined);
  const [anchorNodeId, setAnchorNodeId] = useState<string | null | undefined>(undefined);
  const [subEvents, setSubEvents] = useState<SubEvent[] | undefined>(undefined);
  const [parentEvent, setParentEvent] = useState<ParentEventState>(undefined);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    const controller = new AbortController();
    const client = eventHubCalendarClient();
    const api = client.campaignHierarchy();
    setNodes(undefined);
    setAnchorNodeId(undefined);
    setSubEvents(undefined);
    setParentEvent(undefined);

    const own = api.findEventNode(eventId);
    own.then((n) => hierarchyUnder(api, n)).then(
      (h) => {
        if (cancelled) return;
        setNodes(h.nodes);
        setAnchorNodeId(h.anchorNodeId ?? null);
      },
      () => !cancelled && setNodes(null),
    );
    client.listSubEvents(eventId, { signal: controller.signal }).then(
      (s) => !cancelled && setSubEvents(s),
      () => !cancelled && setSubEvents([]),
    );
    if (knownParent === undefined) {
      own
        .then((n) => parentEventIdOf(api, n))
        .then(async (parentId) => {
          if (!parentId) return null;
          const parent = await client.getEvent(parentId, { signal: controller.signal });
          return { eventId: parentId, eventName: parent.eventName };
        })
        .then(
          (p) => !cancelled && setParentEvent(p),
          () => !cancelled && setParentEvent('error'),
        );
    }

    return () => {
      cancelled = true;
      controller.abort();
    };
    // Not keyed on `knownParent`: it only decides whether to fetch, and is fixed per event.
  }, [eventId]);

  const eventNames = useMemo(() => subEventNames(subEvents), [subEvents]);
  const eventTree = useMemo(
    () => visibleEventTree(eventId, nodes, subEvents),
    [eventId, nodes, subEvents],
  );
  return {
    nodes,
    anchorNodeId,
    eventNames,
    eventTree,
    parentEvent: knownParent !== undefined ? knownParent : parentEvent,
  };
}
